import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// True when at least one of the row's blockers has an owner we could
// nudge. A blocked-by task with no assignee = no one to poke.
export function hasNudgeTarget(blockers) {
    return !!blockers?.some((b) => b.assignees.length > 0);
}
// Sentinel used to key the "no business unit" bucket so it lands at the bottom
// of the grouping with a stable label.
export const NO_BU_KEY = "__no_bu__";
export const NO_BU_LABEL = "Other";
// Helpers shared by item + task sorters. Tasks don't have estimates/due
// dates the same way; we fall back to a large sentinel so they bucket last.
export function dueOrFar(iso) {
    if (!iso)
        return Number.POSITIVE_INFINITY;
    return new Date(iso).getTime();
}
export function nextOrFar(iso) {
    if (!iso)
        return Number.POSITIVE_INFINITY;
    return new Date(iso).getTime();
}
// Item is considered "already on the calendar" when the server reported it
// has scheduled minutes OR a next-scheduled timestamp. We use the dim
// styling + a "Scheduled" header instead of fully hiding it so the user
// can still come back and add another block.
export function isItemScheduled(c) {
    return (c.scheduled_minutes ?? 0) > 0 || !!c.next_scheduled_at;
}
// Same idea for a plain task scheduled directly (not via a checklist item).
export function isTaskScheduled(t) {
    return (t.scheduled_minutes ?? 0) > 0 || !!t.next_scheduled_at;
}
export function fmtMinutes(mins) {
    if (mins <= 0)
        return "0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m)
        return `${h}h ${m}m`;
    if (h)
        return `${h}h`;
    return `${m}m`;
}
export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function fmtDateTime(iso) {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const dStart = new Date(d);
    dStart.setHours(0, 0, 0, 0);
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (dStart.getTime() === today.getTime())
        return `Today ${time}`;
    if (dStart.getTime() === tomorrow.getTime())
        return `Tomorrow ${time}`;
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) + ` ${time}`;
}
export function fmtTimeOfDay(hms) {
    // "09:00:00" -> "9:00 AM" (locale-aware)
    const [h, m] = hms.split(":").map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
// "2026-05-12" -> "May 12" (or "Today"/"Tomorrow"/"Yesterday" when applicable).
export function fmtDueDate(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = date.getTime() - today.getTime();
    const days = Math.round(ms / 86400000);
    if (days === 0)
        return "Today";
    if (days === 1)
        return "Tomorrow";
    if (days === -1)
        return "Yesterday";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
export function isOverdue(iso) {
    if (!iso)
        return false;
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
}
// Small "Open project" affordance — opens the Projects & Meetings page in a
// new tab focused on the given project (the page reads ?project=ID on mount).
// Uses an inline button (not an <a>) so we can stopPropagation against the
// surrounding row's onClick (which would otherwise open the BlockEditor).
export function OpenProjectLink({ projectId, label }) {
    return (_jsx("button", { type: "button", className: "btn-link", onClick: (e) => {
            e.stopPropagation();
            window.open(`/projects?project=${encodeURIComponent(projectId)}`, "_blank", "noopener,noreferrer");
        }, title: "Open this project's full details in a new tab", children: label ?? "Open project ↗" }));
}
export function DueDates({ taskDue, projectDue, }) {
    if (!taskDue && !projectDue)
        return null;
    return (_jsxs("div", { className: "due-dates", children: [taskDue && (_jsxs("span", { className: `pill due-pill ${isOverdue(taskDue) ? "overdue" : ""}`, title: `Task due ${taskDue}`, children: ["Task due ", fmtDueDate(taskDue)] })), projectDue && (_jsxs("span", { className: `pill due-pill ${isOverdue(projectDue) ? "overdue" : ""}`, title: `Project due ${projectDue}`, children: ["Project due ", fmtDueDate(projectDue)] }))] }));
}
export function fmtRecurrence(p) {
    const at = fmtTimeOfDay(p.time_of_day);
    if (p.frequency === "daily")
        return `Daily at ${at}`;
    if (p.frequency === "weekly") {
        const days = (p.days_of_week ?? []).map((i) => DAY_LABELS[i]).join(", ") || "—";
        return `${days} at ${at}`;
    }
    if (p.frequency === "monthly") {
        const dom = p.day_of_month ?? 1;
        return `Monthly on day ${dom} at ${at}`;
    }
    return at;
}
// Same idea as fmtRecurrence, but reads from the admin-set defaults on a
// recurring-event Task (different field names — recurrence_* prefix). Returns
// null when the admin hasn't configured a default yet, so the row can fall
// back to a "Set up your schedule" placeholder instead of nonsense text.
export function fmtTaskRecurrence(t) {
    if (!t.recurrence_frequency || !t.recurrence_time_of_day)
        return null;
    const at = fmtTimeOfDay(t.recurrence_time_of_day);
    let base;
    if (t.recurrence_frequency === "daily") {
        base = `Daily at ${at}`;
    }
    else if (t.recurrence_frequency === "weekly") {
        const days = (t.recurrence_days_of_week ?? []).map((i) => DAY_LABELS[i]).join(", ") || "—";
        base = `${days} at ${at}`;
    }
    else if (t.recurrence_frequency === "monthly") {
        base = `Monthly on day ${t.recurrence_day_of_month ?? 1} at ${at}`;
    }
    else {
        base = at;
    }
    const dur = t.recurrence_duration_minutes;
    if (dur)
        base += ` · ${dur === 60 ? "1 hr" : `${dur} min`}`;
    return base;
}
// How often the panel re-polls /schedulable-items while open. Catches tasks/projects
// the admin (or teammates in other tabs) added without requiring a manual refresh.
export const POLL_MS = 30_000;
// Group the in-scope projects into per-business-unit bins. Projects are sorted
// inside each bin by due-date then priority then name (the same order the
// backend returns), and bins are alphabetized with the no-BU bucket pinned last.
export function buildBins(data) {
    const map = new Map();
    for (const p of data.projects ?? []) {
        const key = p.business_unit_id ?? NO_BU_KEY;
        let bin = map.get(key);
        if (!bin) {
            bin = {
                key,
                label: p.business_unit_id
                    ? p.business_unit_name ?? "Unknown"
                    : NO_BU_LABEL,
                projects: [],
            };
            map.set(key, bin);
        }
        bin.projects.push(p);
    }
    const bins = Array.from(map.values());
    bins.sort((a, b) => {
        if (a.key === NO_BU_KEY)
            return 1;
        if (b.key === NO_BU_KEY)
            return -1;
        return a.label.localeCompare(b.label);
    });
    return bins;
}
export function fmtTodoDue(iso) {
    if (!iso)
        return null;
    // iso is YYYY-MM-DD — parse as local midday to avoid TZ slippage.
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
